import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, X, Loader2, Send, Trash2, ExternalLink } from "lucide-react";
import api from "../../lib/api";
import { photoUrl } from "./InstagramConnect";
import { slotLabel } from "./SocialPerformance";

const toLocalInput = (iso) => { if (!iso) return ""; const d = new Date(iso); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };

export function PostComposer({ post, photoIds, locations, onClose, onSaved }) {
  const [ids, setIds] = useState(post?.photo_ids || photoIds || []);
  const [caption, setCaption] = useState(post?.caption || "");
  const [loc, setLoc] = useState(post?.location || "");
  const [when, setWhen] = useState(toLocalInput(post?.scheduled_at) || toLocalInput(new Date(Date.now() + 86400000).toISOString()));
  const [brief, setBrief] = useState("");
  const [busy, setBusy] = useState(null);
  const [slots, setSlots] = useState([]);
  useEffect(() => { api.get("/social/best-times", { params: { tz_offset_minutes: -new Date().getTimezoneOffset() } }).then(({ data }) => setSlots(data.slots.slice(0, 3))).catch(() => {}); }, []);
  const pickSlot = (s) => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    while ((d.getDay() + 6) % 7 !== s.weekday) d.setDate(d.getDate() + 1);
    d.setHours(s.hour, 0, 0, 0);
    setWhen(toLocalInput(d.toISOString()));
  };
  const locked = post && ["publishing", "published"].includes(post.status);
  useEffect(() => { if (post?.photo_ids) setIds(post.photo_ids); }, [post]);

  const ai = async () => {
    setBusy("ai");
    try { const { data } = await api.post("/social/ai/caption", { photo_ids: ids, location: loc || null, brief }); setCaption(data.caption); }
    catch (e) { toast.error(e?.response?.data?.detail || "AI caption failed"); }
    finally { setBusy(null); }
  };
  const save = async (status) => {
    setBusy(status);
    const body = { photo_ids: ids, caption, location: loc || null, scheduled_at: when ? new Date(when).toISOString() : null, status };
    try {
      if (post?.id) await api.put(`/social/posts/${post.id}`, body); else await api.post("/social/posts", body);
      toast.success(status === "scheduled" ? "Scheduled" : "Saved as draft"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(null); }
  };
  const del = async () => { await api.delete(`/social/posts/${post.id}`); toast.success("Post deleted"); onSaved(); };
  const now = async () => {
    try { await save("scheduled"); await api.post(`/social/posts/${post.id}/publish-now`); toast.success("Publishing to Instagram…"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not publish"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="post-composer">
      <div className="bg-brand-page rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto shadow-2xl grid md:grid-cols-2" onClick={(e) => e.stopPropagation()}>
        <div className="bg-brand-ink/5 p-5 space-y-3">
          <img src={ids[0] ? photoUrl(ids[0]) : ""} alt="" className="w-full aspect-[4/5] object-cover rounded-lg bg-brand-hover" data-testid="composer-preview" />
          {ids.length > 1 && (
            <div className="flex gap-2 overflow-x-auto">
              {ids.map((id, i) => (
                <div key={id} className="relative shrink-0">
                  <img src={photoUrl(id, true)} alt="" className="h-16 w-14 object-cover rounded" />
                  {!locked && <button onClick={() => setIds(ids.filter((x) => x !== id))} className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-black text-white text-[9px]">×</button>}
                  <span className="absolute bottom-0 left-0 text-[9px] bg-black/60 text-white px-1">{i + 1}</span>
                </div>
              ))}
            </div>
          )}
          <p className="text-[11px] text-brand-tertiary">{ids.length === 1 ? "Single photo post" : `Carousel · ${ids.length} photos`}</p>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-2xl text-brand-ink">{post?.id ? "Edit post" : "New post"}</h3>
            <button onClick={onClose} data-testid="composer-close"><X className="h-5 w-5 text-brand-muted" /></button>
          </div>
          {post?.status === "published" && <a href={post.permalink} target="_blank" rel="noreferrer" className="text-sm text-emerald-700 inline-flex items-center gap-1">Live on Instagram <ExternalLink className="h-3 w-3" /></a>}
          {post?.status === "failed" && <p className="text-xs text-rose-700 bg-rose-50 rounded p-2" data-testid="composer-error">Failed: {post.error}</p>}
          <select value={loc} onChange={(e) => setLoc(e.target.value)} disabled={locked} data-testid="composer-location" className="w-full text-sm rounded-lg border border-brand-border bg-brand-surface px-3 py-2">
            <option value="">Brand-wide (no location)</option>{locations.map((l) => <option key={l}>{l}</option>)}
          </select>
          <div className="flex gap-2">
            <input value={brief} onChange={(e) => setBrief(e.target.value)} placeholder="Optional brief for AI (e.g. 'open house Thursday')" data-testid="composer-brief"
              className="flex-1 text-sm rounded-lg border border-brand-border bg-brand-surface px-3 py-2 outline-none" />
            <button onClick={ai} disabled={busy || locked || !ids.length} data-testid="composer-ai" className="inline-flex items-center gap-1.5 rounded-lg bg-brand-gold/15 text-brand-ink px-3 py-2 text-sm hover:bg-brand-gold/25 disabled:opacity-50">
              {busy === "ai" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 text-brand-gold" />} Write it
            </button>
          </div>
          <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={8} disabled={locked} data-testid="composer-caption" placeholder="Caption and hashtags…"
            className="w-full text-sm rounded-lg border border-brand-border bg-brand-surface px-3 py-2 outline-none resize-none" />
          <p className="text-[11px] text-brand-tertiary text-right">{caption.length} / 2,200</p>
          <label className="block text-xs text-brand-muted">Post at
            <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} disabled={locked} data-testid="composer-when"
              className="mt-1 w-full text-sm rounded-lg border border-brand-border bg-brand-surface px-3 py-2" />
          </label>
          {!locked && slots.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5" data-testid="composer-best-times">
              <span className="text-[11px] text-brand-tertiary">Best times:</span>
              {slots.map((s, i) => <button key={i} onClick={() => pickSlot(s)} data-testid={`composer-slot-${i}`} className="rounded-full border border-brand-border px-2.5 py-0.5 text-[11px] text-brand-ink hover:bg-brand-hover">{slotLabel(s)}</button>)}
            </div>
          )}
          {!locked && (
            <div className="flex flex-wrap gap-2 pt-2">
              <button onClick={() => save("scheduled")} disabled={!!busy || !ids.length} data-testid="composer-schedule" className="rounded-full bg-brand-ink text-brand-page px-4 py-2 text-sm disabled:opacity-50">Schedule</button>
              <button onClick={() => save("draft")} disabled={!!busy || !ids.length} data-testid="composer-draft" className="rounded-full border border-brand-border px-4 py-2 text-sm">Save draft</button>
              {post?.id && <button onClick={now} disabled={!!busy} data-testid="composer-publish-now" className="rounded-full border border-brand-border px-4 py-2 text-sm inline-flex items-center gap-1"><Send className="h-3.5 w-3.5" /> Post now</button>}
              {post?.id && <button onClick={del} data-testid="composer-delete" className="ml-auto text-rose-600 text-sm inline-flex items-center gap-1"><Trash2 className="h-3.5 w-3.5" /> Delete</button>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
